import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getInstitutionShortLabel } from '../../utils/institutionAssets';
import { EDVISE_THEME_BLUE } from '../../utils/jupasOrderDisplay';
import JupasSplitPill from './JupasSplitPill';

const INSTITUTION_COLOR = EDVISE_THEME_BLUE;

const PRESTIGE_ORDER = [
  'The University of Hong Kong',
  'The Chinese University of Hong Kong',
  'The Hong Kong University of Science and Technology',
  'City University of Hong Kong',
  'The Hong Kong Polytechnic University',
  'Hong Kong Baptist University',
  'Lingnan University',
  'Hong Kong Shue Yan University',
  'The Education University of Hong Kong',
  'Hong Kong Metropolitan University',
  'The Hang Seng University of Hong Kong',
  'Saint Francis University',
  'Tung Wah College',
  'UOW College Hong Kong',
  'Hong Kong Chu Hai College',
  'Technological and Higher Education Institute of Hong Kong, Vocational Training Council',
];

function sortInstitutions(institutions, language) {
  const prestigeIndex = new Map(PRESTIGE_ORDER.map((name, index) => [name, index]));

  return [...institutions].sort((a, b) => {
    const indexA = prestigeIndex.get(a);
    const indexB = prestigeIndex.get(b);
    if (indexA == null && indexB == null) {
      return getInstitutionShortLabel(a, language).localeCompare(
        getInstitutionShortLabel(b, language),
        language,
      );
    }
    if (indexA == null) return 1;
    if (indexB == null) return -1;
    return indexA - indexB;
  });
}

/**
 * Compact informative selector rail for collapsed Step 2.
 * One pill per institution: short name on the left, selected programme count on the right.
 */
export default function JupasSelectorSidebar({
  selectedProgrammes = [],
  className = '',
}) {
  const { t, i18n } = useTranslation();

  const pills = useMemo(() => {
    const countsByInstitution = new Map();

    selectedProgrammes.forEach((programme) => {
      const institution = programme?.institution;
      if (!institution) return;
      countsByInstitution.set(institution, (countsByInstitution.get(institution) || 0) + 1);
    });

    if (countsByInstitution.size === 0) {
      return [{
        key: 'no-programmes',
        left: t('jupa.workspace.openProgrammes'),
        right: '0',
        variant: 'warning',
        title: t('jupa.workspace.noProgrammesYet'),
      }];
    }

    return sortInstitutions([...countsByInstitution.keys()], i18n.language).map((institution) => {
      const shortLabel = getInstitutionShortLabel(institution, i18n.language);
      const count = countsByInstitution.get(institution);
      return {
        key: institution,
        left: shortLabel,
        right: String(count),
        color: INSTITUTION_COLOR,
        variant: 'score',
        title: `${shortLabel} · ${count}`,
      };
    });
  }, [i18n.language, selectedProgrammes, t]);

  return (
    <div className={`flex min-h-0 select-none flex-col gap-1.5 overflow-y-auto ${className}`}>
      {pills.map((pill) => (
        <JupasSplitPill
          key={pill.key}
          left={pill.left}
          right={pill.right}
          leftColor={pill.color}
          variant={pill.variant}
          title={pill.title}
        />
      ))}
    </div>
  );
}
